import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap } from 'rxjs/operators';
import moment, { Moment } from 'moment';

import {
  ClientReportDefaultsGQL,
  ClientReportDefaultsQuery,
  ClientReportPreviewGQL,
  ReportChecklistItemTypeChoice,
  ReportChecklistItemsGQL,
  ReportContentInput,
  SendClientReportGQL,
} from '../../../../generated/graphql';
import { NotificationService } from '../../../shared/notification.service';
import { RelayIdService } from '../../../shared/relay-id.service';

const DATE_FORMAT = 'YYYY-MM-DD';
const PREVIEW_DEBOUNCE_MS = 600;
const ERROR_LEVEL = 2;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ChecklistItem {
  id: string;
  title: string;
}

/**
 * "Report" tab: pick a period, write the texts, tick items, choose recipients and send.
 * Nothing is saved until the report is sent; after sending the form starts over.
 */
@Component({
  selector: 'app-client-report-editor',
  templateUrl: './client-report-editor.component.html',
  styleUrls: ['./client-report-editor.component.scss'],
})
export class ClientReportEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orderId: string;
  /** [dateFrom, dateTo] as YYYY-MM-DD, handed over from the Stats OLD tab. */
  @Input() period: string[] = [];
  /** Emitted after the report was sent. */
  @Output() sent = new EventEmitter<void>();

  readonly separatorKeysCodes: number[] = [ENTER, COMMA, SEMICOLON];

  defaults: ClientReportDefaultsQuery['clientReportDefaults'];
  actions: ChecklistItem[] = [];
  nextSteps: ChecklistItem[] = [];
  selectedActions = new Set<string>();
  selectedNextSteps = new Set<string>();
  emails: string[] = [];
  invalidEmail = '';
  dateFrom: string = null;
  dateTo: string = null;

  texts = new FormGroup({
    intro: new FormControl(''),
    conclusion: new FormControl(''),
  });
  periodForm = new FormGroup({
    start: new FormControl<Moment | null>(null),
    end: new FormControl<Moment | null>(null),
  });

  previewHtml: SafeHtml;
  editingPeriod = false;
  showPreview = true;
  sending = false;

  private preview$ = new Subject<void>();
  private subscriptions = new Subscription();

  constructor(
    private defaultsGQL: ClientReportDefaultsGQL,
    private previewGQL: ClientReportPreviewGQL,
    private sendGQL: SendClientReportGQL,
    private checklistGQL: ReportChecklistItemsGQL,
    private relayIdService: RelayIdService,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer,
  ) {}

  /** The form is shown once the defaults are loaded and a period is picked. */
  get ready(): boolean {
    return Boolean(this.defaults && this.dateFrom && this.dateTo);
  }

  ngOnInit(): void {
    // Checklist titles come in the report's language (known from the defaults), as the client will read them
    this.subscriptions.add(
      this.defaultsGQL
        .fetch({ orderId: this.orderRelayId() })
        .pipe(
          switchMap(({ data }) => {
            this.defaults = data.clientReportDefaults;
            this.reset();
            return this.checklistGQL.fetch({ language: this.defaults.language });
          }),
        )
        .subscribe(({ data }) => {
          const items = (data.reportChecklistItems?.edges || []).map((edge) => edge?.node).filter(Boolean);
          this.actions = items.filter((item) => item.type === ReportChecklistItemTypeChoice.Action);
          this.nextSteps = items.filter((item) => item.type === ReportChecklistItemTypeChoice.NextStep);
        }),
    );
    // switchMap drops a slow preview when a newer one is requested
    this.subscriptions.add(
      this.preview$
        .pipe(
          debounceTime(PREVIEW_DEBOUNCE_MS),
          switchMap(() =>
            this.previewGQL.fetch({ report: this.content() }, { fetchPolicy: 'no-cache' }).pipe(
              catchError((error) => {
                this.notificationService.notify(error.message, ERROR_LEVEL);
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe(({ data }) => {
          this.previewHtml = this.sanitizer.bypassSecurityTrustHtml(data.clientReportPreview || '');
        }),
    );
    this.subscriptions.add(this.texts.valueChanges.subscribe(() => this.refreshPreview()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.period && this.period?.length === 2) {
      this.setPeriod(this.period[0], this.period[1]);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  applyPickedPeriod(): void {
    const { start, end } = this.periodForm.value;
    if (start && end) {
      this.setPeriod(moment(start).format(DATE_FORMAT), moment(end).format(DATE_FORMAT));
    }
  }

  toggleItem(selected: Set<string>, id: string): void {
    if (selected.has(id)) {
      selected.delete(id);
    } else {
      selected.add(id);
    }
    this.refreshPreview();
  }

  addEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }
    if (!EMAIL_PATTERN.test(value)) {
      this.invalidEmail = value;
      return;
    }
    if (!this.emails.some((email) => email.toLowerCase() === value.toLowerCase())) {
      this.emails.push(value);
    }
    this.invalidEmail = '';
    event.chipInput?.clear();
  }

  removeEmail(email: string): void {
    this.emails = this.emails.filter((item) => item !== email);
  }

  /** Back to the starting texts, ticks and recipients. */
  reset(): void {
    this.texts.setValue({ intro: this.defaults.intro, conclusion: '' }, { emitEvent: false });
    this.selectedActions = new Set(this.defaults.actionIds);
    this.selectedNextSteps = new Set(this.defaults.nextStepIds);
    this.emails = [...this.defaults.recipients];
    this.invalidEmail = '';
    this.refreshPreview();
  }

  send(): void {
    this.sending = true;
    this.sendGQL
      .mutate({ report: this.content(), emails: this.emails })
      .pipe(finalize(() => (this.sending = false)))
      .subscribe({
        next: () => {
          this.notificationService.notify('Sent successfully!');
          this.dateFrom = this.dateTo = null;
          this.periodForm.reset();
          this.reset();
          this.sent.emit();
        },
        error: (error) => this.notificationService.notify(error.message, ERROR_LEVEL),
      });
  }

  /** Grow the preview iframe to its content so the e-mail reads without a nested scrollbar. */
  fitPreview(frame: HTMLIFrameElement): void {
    const height = frame.contentWindow?.document?.body?.scrollHeight;
    if (height) {
      frame.style.height = `${height}px`;
    }
  }

  private setPeriod(dateFrom: string, dateTo: string): void {
    this.dateFrom = dateFrom;
    this.dateTo = dateTo;
    this.editingPeriod = false;
    this.periodForm.setValue({ start: moment(dateFrom), end: moment(dateTo) }, { emitEvent: false });
    this.refreshPreview();
  }

  private refreshPreview(): void {
    if (this.ready) {
      this.preview$.next();
    }
  }

  private content(): ReportContentInput {
    return {
      orderId: this.orderRelayId(),
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      intro: this.texts.value.intro || '',
      conclusion: this.texts.value.conclusion || '',
      actionIds: [...this.selectedActions],
      nextStepIds: [...this.selectedNextSteps],
    };
  }

  private orderRelayId(): string {
    return this.relayIdService.getRelayId(Number(this.orderId), 'OrderMType');
  }
}
